import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import * as routes from 'router/routes';
import { navigate } from 'router/navigation';
import { API, API_ERROR_CODES, API_ERROR_STATUS, REQUEST_METHODS } from 'constants/api';
import { API_TAG_TYPES } from 'constants/apiTagTypes';
import { LOCAL_STORAGE_KEY } from 'constants/localStorage';
import { logout } from 'store/entities/users/slice';

const mutex = new Mutex();

const setAuthHeaders = (headers, extraOptions) => {
  const { isPublic = false, isMfa = false } = extraOptions;
  const accessToken = localStorage.getItem(LOCAL_STORAGE_KEY.AUTH_TOKEN);

  if (!isPublic && !isMfa && accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
};

const setMfaAuthHeaders = (headers, extraOptions) => {
  const { isMfa = false } = extraOptions;
  const mfaToken = localStorage.getItem(LOCAL_STORAGE_KEY.MFA_TOKEN);

  if (isMfa && mfaToken) {
    headers.set('Mfa-Token', mfaToken);
  }
};

const fetchBaseQueryWithHeaders = async (args, api, extraOptions) =>
  fetchBaseQuery({
    baseUrl: API,
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
      setAuthHeaders(headers, extraOptions);
      setMfaAuthHeaders(headers, extraOptions);

      return headers;
    },
  })(args, api, extraOptions);

export const baseQuery = async (args, api, extraOptions = {}) => {
  try {
    await mutex.waitForUnlock();

    const { getState, dispatch } = api;
    const store = getState();
    const { currentUser = {} } = store.usersSlice;

    const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN);

    let response = await fetchBaseQueryWithHeaders(args, api, extraOptions);

    if (response?.error?.status === API_ERROR_STATUS.UNAUTHORIZED) {
      if (extraOptions.isMfa || !refreshToken) {
        dispatch(logout());
      } else if (!mutex.isLocked()) {
        const release = await mutex.acquire();

        try {
          const refetchResult = await fetchBaseQueryWithHeaders(
            {
              url: '/userprofile/token/refresh/',
              method: REQUEST_METHODS.POST,
              body: { refresh: refreshToken },
            },
            api,
            extraOptions,
          );

          const { data: refreshData } = refetchResult;

          if (refreshData) {
            const { access, refresh } = refreshData;

            await localStorage.setItem(LOCAL_STORAGE_KEY.AUTH_TOKEN, access);
            await localStorage.setItem(LOCAL_STORAGE_KEY.REFRESH_TOKEN, refresh);

            response = await fetchBaseQueryWithHeaders(args, api, extraOptions);
          } else {
            dispatch(logout());
          }
        } finally {
          release();
        }
      } else {
        await mutex.waitForUnlock();
        response = await fetchBaseQueryWithHeaders(args, api, extraOptions);
      }
    }

    if (response.error?.status === API_ERROR_STATUS.FORBIDDEN) {
      const code = response.error.data?.code;

      if (code === API_ERROR_CODES.UNAUTHORIZED_ACCESS) {
        navigate(routes.home);
      }

      if ([API_ERROR_CODES.ORGANISATION_ARCHIVED, API_ERROR_CODES.ORGANISATION_DELETED].includes(code)) {
        if (!currentUser.is_superadmin) {
          api.util.invalidateTags([{ type: API_TAG_TYPES.CURRENT_USER }]);
        }

        navigate(routes.home);
      }
    }

    return response;
  } catch (error) {
    return { error };
  }
};
